import { type PropsWithChildren, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { usePlaygroundParametersStore } from "@/atoms/playground";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { customizationApi } from "@/services/customization-service";
import { organizationApi } from "@/services/organization-service";
import { changeLanguage, normalizeOptionalLanguage } from "@/utils/language";

const getOrganizationLanguage = (organization?: {
  default_language?: string | null;
  language?: string | null;
} | null) =>
  normalizeOptionalLanguage(
    organization?.default_language || organization?.language,
  );

let platformLanguageRequest: ReturnType<
  typeof customizationApi.getDefaultLanguage
> | null = null;
const organizationLanguageRequests = new Map<
  string,
  ReturnType<typeof organizationApi.getOrganization>
>();

const loadPlatformLanguage = () => {
  if (!platformLanguageRequest) {
    const request = customizationApi.getDefaultLanguage();
    platformLanguageRequest = request;
    request.then(
      () => {
        if (platformLanguageRequest === request) platformLanguageRequest = null;
      },
      () => {
        if (platformLanguageRequest === request) platformLanguageRequest = null;
      },
    );
  }

  return platformLanguageRequest;
};

const loadOrganizationLanguage = (organizationId: string) => {
  const existingRequest = organizationLanguageRequests.get(organizationId);
  if (existingRequest) return existingRequest;

  const request = organizationApi.getOrganization(organizationId);
  organizationLanguageRequests.set(organizationId, request);
  request.then(
    () => {
      if (organizationLanguageRequests.get(organizationId) === request) {
        organizationLanguageRequests.delete(organizationId);
      }
    },
    () => {
      if (organizationLanguageRequests.get(organizationId) === request) {
        organizationLanguageRequests.delete(organizationId);
      }
    },
  );

  return request;
};

/**
 * Resolves authenticated application language independently of either router.
 * Persisted i18n state remains usable while organization bootstrap is pending;
 * children are never blocked by a language lookup.
 */
export const ApplicationLanguageBootstrap = ({
  children,
}: PropsWithChildren) => {
  const { i18n } = useTranslation();
  const { currentUser, userProfile, cache } = useAuth();
  const { currentOrganization, initialized } = useOrganization();
  const setPlaygroundLanguage = usePlaygroundParametersStore(
    (state) => state.setLanguage,
  );
  const resolutionVersionRef = useRef(0);

  const userLanguage = normalizeOptionalLanguage(
    userProfile?.language || cache?.preferences?.language,
  );
  const organizationId = currentOrganization?.id;
  const organizationLanguage = getOrganizationLanguage(currentOrganization);

  useEffect(() => {
    const resolutionVersion = ++resolutionVersionRef.current;

    if (!currentUser || cache === undefined) return;

    const isCurrent = () =>
      resolutionVersion === resolutionVersionRef.current;

    const applyResolvedLanguage = async (language: string) => {
      if (!isCurrent()) return;

      const activeLanguage = normalizeOptionalLanguage(
        i18n.resolvedLanguage || i18n.language,
      );
      if (activeLanguage === language) return;

      setPlaygroundLanguage(language);
      await changeLanguage(language, { markAsUserChange: false });
    };

    const resolveLanguage = async () => {
      if (userLanguage) {
        await applyResolvedLanguage(userLanguage);
        return;
      }

      // Wait for authoritative organization restoration before considering a
      // lower-priority platform fallback. This avoids a visible language flip.
      if (!initialized) return;

      if (organizationLanguage) {
        await applyResolvedLanguage(organizationLanguage);
        return;
      }

      if (organizationId) {
        try {
          const { data } = await loadOrganizationLanguage(organizationId);
          if (!isCurrent()) return;

          const fetchedOrganizationLanguage = getOrganizationLanguage(data);
          if (fetchedOrganizationLanguage) {
            await applyResolvedLanguage(fetchedOrganizationLanguage);
            return;
          }
        } catch (error) {
          console.error("Error loading selected organization language:", error);
          if (!isCurrent()) return;
        }
      }

      try {
        const { data } = await loadPlatformLanguage();
        if (!isCurrent()) return;

        await applyResolvedLanguage(
          normalizeOptionalLanguage(data?.default_language) || "en",
        );
      } catch (error) {
        console.error("Error loading platform default language:", error);
        if (isCurrent()) await applyResolvedLanguage("en");
      }
    };

    void resolveLanguage();

    return () => {
      // Invalidating the generation prevents an older organization/platform
      // request from applying after a user preference or organization change.
      if (resolutionVersionRef.current === resolutionVersion) {
        resolutionVersionRef.current += 1;
      }
    };
  }, [
    cache,
    currentUser,
    i18n,
    initialized,
    organizationId,
    organizationLanguage,
    setPlaygroundLanguage,
    userLanguage,
  ]);

  return children;
};
